import { useEffect, useMemo, useState } from "react"

import { App, Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import ExportDropdown from "../../../../Components/Export"
import { COMPARE_IDS, getCompareParams } from "../../../../Components/Filters/CompareSelect/presets"
import { DashboardFiltersBlock } from "../../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import Private from "../../../../Components/Private"
import { SubtitleSection } from "../../../../Components/SubtitleSection/SubtitleSection"
import { ErrorsActions } from "../../../../Redux/redux/errors.slice"
import { getDates } from "../../../../Utils/utils"
import { getUsageOverview, postUsageOverviewExport } from "../../../Balance/services"
import { usePageQuery } from "../../usePageQuery"
import { formatUsageMoney } from "./chartOptions"
import UsageOverviewChart from "./UsageOverviewChart"

import "./UsageOverview.less"

const { Text } = Typography

const DEFAULT_FILTERS = {
  period: "27 days",
  compare: { mode: COMPARE_IDS.PREV_PERIOD }
}

const UsageOverview = () => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const dispatch = useDispatch()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const currentUserId = useSelector((state) => state.auth.userInfo?.id)

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const handleChangeFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }))

  const { search } = useLocation()
  const { user_id } = usePageQuery(search)

  const queryParams = useMemo(() => {
    const period_query = getDates(filters.period)
    const compare_query = getCompareParams(filters?.compare, period_query)
    return {
      ...period_query,
      ...compare_query,
      ...(user_id || currentUserId ? { user_id: user_id || currentUserId } : {})
    }
  }, [filters, user_id, currentUserId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getUsageOverview(queryParams)
      .then((res) => {
        if (cancelled) return
        if (res?.ok) setData(res.data)
        else dispatch(ErrorsActions.errorSave(res?.data))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [queryParams, dispatch])

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const res = await postUsageOverviewExport(queryParams)
      if (res?.ok) {
        notification.success({
          message: t("Exporting"),
          description: res.data?.message,
          closeIcon: <Text>Dismiss</Text>,
          style: { minWidth: "max-content", width: 475 }
        })
      } else {
        dispatch(ErrorsActions.errorSave(res?.data))
      }
    } finally {
      setExporting(false)
    }
  }

  const compareActive = !!filters?.compare && !!data?.comparison_period
  const compareLabel = data?.comparison_period?.label

  return (
    <div className="billing-usage-overview">
      <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
        <SubtitleSection
          noMargin
          isMobile={isMobile}
          title={t("Usage overview")}
          description={t("Daily breakdown of alert costs.")}
        />

        <Flex
          gap={8}
          align={isMobile ? "stretch" : "center"}
          wrap="wrap"
          vertical={isMobile}
          style={{ width: isMobile ? "100%" : undefined }}
        >
          <ExportDropdown
            loading={exporting}
            onExport={handleExport}
            isAdmin={false}
            fullWidths={isMobile}
            height={40}
          />
          <DashboardFiltersBlock
            onlyPeriod
            showCompare
            filters={filters}
            handleChangeFilter={handleChangeFilter}
          />
        </Flex>
      </Flex>

      <Flex
        gap={36}
        wrap="wrap"
        className="billing-usage-overview__totals"
        style={{ marginTop: 16 }}
      >
        <TotalBlock
          title="Cost"
          color="#225AEA"
          value={data?.cost_total}
          comparisonValue={compareActive ? data?.comparison?.cost_total : null}
          comparisonLabel={compareLabel}
        />
        <TotalBlock
          title="Credited"
          color="#DF7009"
          value={data?.credited_total}
          comparisonValue={compareActive ? data?.comparison?.credited_total : null}
          comparisonLabel={compareLabel}
        />
      </Flex>

      <Spin spinning={loading}>
        <UsageOverviewChart data={data} />
      </Spin>
    </div>
  )
}

const TotalBlock = ({ title, color, value, comparisonValue, comparisonLabel }) => (
  <Flex gap={8} align="flex-start" className="billing-usage-overview__total">
    <div className="billing-usage-overview__total-marker" style={{ background: color }} />
    <Flex gap={4} vertical>
      <Text type="secondary">{title}</Text>
      <Text strong className="billing-usage-overview__total-value">
        {value != null ? <Private>{formatUsageMoney(value)}</Private> : "-"}
      </Text>
      {comparisonValue != null && (
        <Text type="secondary" className="billing-usage-overview__total-comp">
          <Private>{formatUsageMoney(comparisonValue)}</Private>{" "}
          {comparisonLabel || "previous period"}
        </Text>
      )}
    </Flex>
  </Flex>
)

export default UsageOverview
